import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./FormClientContract.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";
import { useDropzone } from "react-dropzone";

const animatedComponents = makeAnimated();

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const formatAmountDisplay = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0,00";

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

// Move statusOptions outside the component
const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Completed", label: "Completed" },
  { value: "Suspended", label: "Suspended" },
  { value: "Cancelled", label: "Cancelled" },
];

const CreateClientAddendum = () => {
  const navigate = useNavigate();
  const { contractId } = useParams();

  const [parentContract, setParentContract] = useState(null);

  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contractOpportunities, setContractOpportunities] = useState([]);
  const [projectCodeOptions, setProjectCodeOptions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProjectCode, setSelectedProjectCode] = useState(null);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [selectedOpps, setSelectedOpps] = useState([]);
  const [selectedSignDate, setSelectedSignDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [files, setFiles] = useState([]);
  const [dropzoneKey, setDropzoneKey] = useState(Date.now());

  const [errorNotification, setErrorNotification] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isOppLoading, setIsOppLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    contract: true,
    opportunities: true,
    attachments: true,
  });

  const [formData, setFormData] = useState({
    nr: "",
    client_document: "",
    sign_date: "",
    end_date: "",
    responsible_person: "",
    client: "",
    project_code: "",
    object: "",
    status: "",
  });

  const [lines, setLines] = useState([]);

  const isFrameworkAgreement =
    parentContract?.contract_type === "Framework Agreement";

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const showError = useCallback((message, timeout = 5000) => {
    setErrorNotification(message);
    setTimeout(() => setErrorNotification(""), timeout);
  }, []);

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const m = new Map();

    for (const r of exchangeRates) {
      const curId = r.currency;
      if (!m.has(curId)) m.set(curId, []);

      m.get(curId).push({
        value: r.id,
        label: String(r.value ?? ""),
        dateLabel: r.date || "",
        rateValue: Number(r.value),
        rateDate: r.date,
      });
    }

    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      m.set(k, arr);
    }

    return m;
  }, [exchangeRates]);

  const getDefaultExchangeRateOption = useCallback(
    (currencyId) => {
      const arr = exchangeOptionsByCurrencyId.get(currencyId) || [];
      return arr.length ? arr[0] : null;
    },
    [exchangeOptionsByCurrencyId]
  );

  const computeSystemAmount = useCallback(
    (sum, currencyId, exchangeOpt) => {
      if (sum == null || sum === "") return "";

      const s = Number(sum);
      if (!Number.isFinite(s)) return "";

      if (!currencyId || !systemCurrencyId) return "";

      if (Number(currencyId) === Number(systemCurrencyId)) return s.toFixed(2);

      const rateVal = exchangeOpt?.rateValue;
      if (!rateVal || !Number.isFinite(rateVal)) return "";

      return (s * rateVal).toFixed(2);
    },
    [systemCurrencyId]
  );

  const totalAddendumDisplay = useMemo(() => {
    if (!lines.length) return "0,00";

    const validLines = lines.filter((line) => {
      const hasTotal =
        line.total !== null && line.total !== undefined && line.total !== "";
      const hasCurrency = !!line.currency?.value;
      return hasTotal && hasCurrency;
    });

    if (!validLines.length) return "0,00";

    const distinctCurrencyIds = [
      ...new Set(validLines.map((line) => line.currency.value)),
    ];

    if (distinctCurrencyIds.length === 1) {
      const total = validLines.reduce((acc, line) => {
        const val = Number(line.total);
        return acc + (Number.isFinite(val) ? val : 0);
      }, 0);

      const currencyLabel = validLines[0]?.currency?.label || "";
      return `${formatAmountDisplay(total)} ${currencyLabel}`.trim();
    }

    const totalSystem = validLines.reduce((acc, line) => {
      const val = Number(line.system_total);
      return acc + (Number.isFinite(val) ? val : 0);
    }, 0);

    return `${formatAmountDisplay(totalSystem)} ${systemCurrencyLabel || ""}`.trim();
  }, [lines, systemCurrencyLabel]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);

      try {
        const [
          parentContractRes,
          clientsRes,
          employeesRes,
          currenciesRes,
          exchangeRes,
          systemRes,
        ] = await Promise.all([
          axiosInstance.get(`/client-contracts/${contractId}/`),
          axiosInstance.get("/clients/"),
          axiosInstance.get("/employees/"),
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
        ]);

        const parentData = parentContractRes.data;
        setParentContract(parentData);

        const formattedClients = (clientsRes.data || []).map((c) => ({
          value: c.id,
          label: c.name ?? `Client ${c.id}`,
        }));

        const formattedEmployees = (employeesRes.data || []).map((emp) => ({
          value: emp.id,
          label:
            `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
            `Employee ${emp.id}`,
        }));

        const formattedCurrencies = (currenciesRes.data || []).map((curr) => ({
          value: curr.id,
          label: curr.abbreviation,
          abbreviation: curr.abbreviation,
          name: curr.name,
        }));

        setClients(formattedClients);
        setEmployees(formattedEmployees);
        setCurrencies(formattedCurrencies);

        setExchangeRates(
          (exchangeRes.data || []).sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );

        if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
          const currencyId = systemRes.data[0]?.currency ?? null;
          setSystemCurrencyId(currencyId || null);

          const sys = formattedCurrencies.find(
            (c) => Number(c.value) === Number(currencyId)
          );

          setSystemCurrencyLabel(sys?.abbreviation || "");
        }

        const parentClientId =
          typeof parentData.client === "object"
            ? parentData.client?.id
            : parentData.client_id ?? parentData.client ?? "";

        const clientOption =
          formattedClients.find(
            (c) => Number(c.value) === Number(parentClientId)
          ) ||
          (parentClientId
            ? {
              value: Number(parentClientId),
              label: parentData.client_name || `Client ${parentClientId}`,
            }
            : null);

        setSelectedClient(clientOption);

        setFormData((prev) => ({
          ...prev,
          client: parentClientId ? Number(parentClientId) : "",
        }));

        // Set status from parent contract
        if (parentData.status) {
          const statusOption = STATUS_OPTIONS.find(
            (s) => s.value === parentData.status
          );
          if (statusOption) {
            setSelectedStatus(statusOption);
            setFormData((prev) => ({
              ...prev,
              status: parentData.status,
            }));
          }
        }
      } catch (error) {
        console.error("Initial load error:", error.response?.data || error.message);
        showError("Failed to load addendum form.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [contractId, showError]); // STATUS_OPTIONS is now a constant outside the component, so no need to include it

  useEffect(() => {
    if (!formData.client || !parentContract || !contractId) {
      setContractOpportunities([]);
      setProjectCodeOptions([]);
      setSelectedProjectCode(null);

      setFormData((prev) => ({
        ...prev,
        project_code: "",
      }));

      return;
    }

    const inheritedProjectCode =
      parentContract.project_code_id ??
      parentContract.project_code ??
      "";

    const inheritedProjectCodeLabel =
      parentContract.project_code_label || "";

    setIsOppLoading(true);

    // =====================================================
    // FRAMEWORK AGREEMENT
    // Project Code-urile se pot selecta din CodeRegistry
    // =====================================================
    if (parentContract.contract_type === "Framework Agreement") {
      Promise.all([
        axiosInstance.get(
          `/code_registries/?client=${formData.client}`
        ),
        axiosInstance.get(
          `/opportunities/?client=${formData.client}&for_contract=1`
        ),
      ])
        .then(([projectCodesResponse, opportunitiesResponse]) => {
          const projectCodesData = Array.isArray(projectCodesResponse.data)
            ? projectCodesResponse.data
            : projectCodesResponse.data?.results || [];

          const allProjectCodes = projectCodesData
            .map((pc) => ({
              value: pc.id,
              label: pc.code,
            }))
            .sort((a, b) =>
              (a.label || "").localeCompare(b.label || "")
            );

          setProjectCodeOptions(allProjectCodes);

          // Dacă Framework-ul părinte are deja Project Code,
          // îl preselectăm, dar utilizatorul îl poate schimba.
          if (inheritedProjectCode) {
            const inheritedOption =
              allProjectCodes.find(
                (pc) =>
                  Number(pc.value) === Number(inheritedProjectCode)
              ) || {
                value: Number(inheritedProjectCode),
                label:
                  inheritedProjectCodeLabel ||
                  `Project Code ${inheritedProjectCode}`,
              };

            setSelectedProjectCode(inheritedOption);

            setFormData((prev) => ({
              ...prev,
              project_code: Number(inheritedProjectCode),
            }));
          } else {
            setSelectedProjectCode(null);

            setFormData((prev) => ({
              ...prev,
              project_code: "",
            }));
          }

          const opportunitiesData = Array.isArray(
            opportunitiesResponse.data
          )
            ? opportunitiesResponse.data
            : opportunitiesResponse.data?.results || [];

          const formatted = opportunitiesData.map((opp) => ({
            value: opp.id,
            label:
              `${opp.code_opportunity ?? ""} - ${opp.title_opportunity ?? ""
                }`.trim() ||
              opp.project_name ||
              `Opportunity ${opp.id}`,
            project_code: opp.project_code,
            project_code_label: opp.project_code_label || "",
            subproject_code: opp.subproject_code,
            subproject_code_label:
              opp.subproject_code_label || "",
            client: opp.client,
          }));

          setContractOpportunities(formatted);
        })
        .catch((error) => {
          console.error(
            "Framework Agreement data fetch error:",
            error
          );

          setContractOpportunities([]);
          setProjectCodeOptions([]);
        })
        .finally(() => {
          setIsOppLoading(false);
        });

      return;
    }

    // =====================================================
    // CONTRACT + PURCHASE ORDER
    // Project Code-ul rămâne cel din contractul părinte
    // =====================================================
    if (inheritedProjectCode) {
      const projectCodeOption = {
        value: Number(inheritedProjectCode),
        label:
          inheritedProjectCodeLabel ||
          `Project Code ${inheritedProjectCode}`,
      };

      setProjectCodeOptions([projectCodeOption]);
      setSelectedProjectCode(projectCodeOption);

      setFormData((prev) => ({
        ...prev,
        project_code: Number(inheritedProjectCode),
      }));
    } else {
      setProjectCodeOptions([]);
      setSelectedProjectCode(null);

      setFormData((prev) => ({
        ...prev,
        project_code: "",
      }));
    }

    axiosInstance
      .get(
        `/opportunities/?contract=${contractId}&for_addendum=1`
      )
      .then((response) => {
        const responseData = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        const formatted = responseData.map((opp) => ({
          value: opp.id,
          label:
            `${opp.code_opportunity ?? ""} - ${opp.title_opportunity ?? ""
              }`.trim() ||
            opp.project_name ||
            `Opportunity ${opp.id}`,
          project_code: opp.project_code,
          project_code_label: opp.project_code_label || "",
          subproject_code: opp.subproject_code,
          subproject_code_label:
            opp.subproject_code_label || "",
          client: opp.client,
        }));

        setContractOpportunities(formatted);
      })
      .catch((error) => {
        console.error(
          "Contract opportunities fetch error:",
          error
        );

        setContractOpportunities([]);
      })
      .finally(() => {
        setIsOppLoading(false);
      });
  }, [
    formData.client,
    parentContract,
    contractId,
  ]);

  const filteredOpportunitiesByProjectCode = useMemo(() => {
    if (!isFrameworkAgreement) {
      return contractOpportunities;
    }

    if (!selectedProjectCode) {
      return contractOpportunities;
    }

    return contractOpportunities.filter(
      (opp) =>
        Number(opp.project_code) ===
        Number(selectedProjectCode.value)
    );
  }, [
    contractOpportunities,
    selectedProjectCode,
    isFrameworkAgreement,
  ]);

  const handleClientChange = (selectedOption) => {
    setSelectedClient(selectedOption);
    setSelectedProjectCode(null);
    setSelectedOpps([]);
    setLines([]);

    setFormData((prev) => ({
      ...prev,
      client: selectedOption ? selectedOption.value : "",
      project_code: "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      client: "",
      project_code: "",
      lines: "",
    }));
  };

  const handleProjectCodeChange = (selectedOption) => {
    setSelectedProjectCode(selectedOption);
    setSelectedOpps([]);
    setLines([]);

    setFormData((prev) => ({
      ...prev,
      project_code: selectedOption ? selectedOption.value : "",
    }));

    setFieldErrors((prev) => ({
      ...prev,
      project_code: "",
      lines: "",
    }));
  };

  const handleOppsMultiChange = (opts) => {
    const selected = opts || [];
    setSelectedOpps(selected);

    setLines((prevLines) => {
      const prevById = new Map(prevLines.map((l) => [l.opportunityId, l]));
      const next = [];

      for (const o of selected) {
        const existing = prevById.get(o.value);

        if (existing) {
          next.push(existing);
        } else {
          next.push({
            opportunityId: o.value,
            opportunityLabel: o.label,
            subproject_code_text: o.subproject_code_label || "",
            currency: null,
            exchange_rate: null,
            total: null,
            system_total: "",
          });
        }
      }

      return next;
    });

    setFieldErrors((prev) => ({
      ...prev,
      lines: "",
    }));
  };

  const updateLine = (opportunityId, patch) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.opportunityId !== opportunityId) return l;

        const next = { ...l, ...patch };
        const currencyId = next.currency?.value ?? null;

        if (
          currencyId &&
          systemCurrencyId &&
          Number(currencyId) !== Number(systemCurrencyId)
        ) {
          if (!next.exchange_rate) {
            next.exchange_rate = getDefaultExchangeRateOption(currencyId);
          }
        } else {
          next.exchange_rate = null;
        }

        next.system_total = computeSystemAmount(
          next.total,
          currencyId,
          next.exchange_rate
        );

        return next;
      })
    );
  };

  const removeOpportunity = (opportunityId) => {
    setSelectedOpps((prev) => prev.filter((o) => o.value !== opportunityId));
    setLines((prev) => prev.filter((l) => l.opportunityId !== opportunityId));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      "image/jpeg": [],
      "image/png": [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/plain": [],
    },
    multiple: true,
    maxSize: 1024 * 1024 * 100,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const tooLarge = rejectedFiles.filter(
          (f) => f.errors[0]?.code === "file-too-large"
        );

        if (tooLarge.length > 0) {
          showError(`File ${tooLarge[0].file.name} exceeds 100MB.`, 3000);
        }
      }

      if (acceptedFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      }
    },
  });

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);

      if (updatedFiles.length === 0) {
        setDropzoneKey(Date.now());
      }

      return updatedFiles;
    });
  };

  const handleFileClick = (file) => {
    const url = URL.createObjectURL(file);

    if (file.type === "application/pdf") {
      const newWindow = window.open();

      if (!newWindow) {
        showError("Popup blocked. Please allow popups for PDF preview.");
        URL.revokeObjectURL(url);
        return;
      }

      newWindow.document.write(`
        <html>
          <head>
            <title>${file.name}</title>
            <style>
              body { margin: 0; height: 100vh; }
              embed { width: 100%; height: 100%; }
            </style>
          </head>
          <body>
            <embed src="${url}" type="application/pdf" />
          </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  };

  const getFileIcon = (file) => {
    const fileType = file.type;

    if (fileType === "application/pdf") {
      return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
    }

    if (
      fileType === "application/msword" ||
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
    }

    if (
      fileType === "application/vnd.ms-excel" ||
      fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
    }

    if (fileType.startsWith("image/")) {
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
    }

    return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
  };

  const validateFiles = () => {
    if (files.length === 0) return true;

    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      showError(
        `The following files exceed the 100MB limit:\n${oversizedFiles
          .map((f) => f.name)
          .join("\n")}`,
        5000
      );
      return false;
    }

    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nr?.trim()) newErrors.nr = "Required";
    if (!formData.client_document?.trim()) newErrors.client_document = "Required";
    if (!formData.responsible_person) newErrors.responsible_person = "Required";
    if (!formData.client) newErrors.client = "Required";
    if (!isFrameworkAgreement && !formData.project_code) {
      newErrors.project_code = "Required";
    }
    if (!formData.sign_date) newErrors.sign_date = "Required";
    if (!formData.end_date) newErrors.end_date = "Required";
    if (!formData.object?.trim()) newErrors.object = "Required";
    if (!isFrameworkAgreement && !lines.length) {
      newErrors.lines = "Select at least one opportunity";
    }

    lines.forEach((l, idx) => {
      if (l.total !== null && l.total !== undefined && l.total !== "") {
        if (!l.currency) {
          newErrors[`line_${idx}_currency`] = "Currency required";
        }

        const currencyId = l.currency?.value ?? null;

        if (
          currencyId &&
          systemCurrencyId &&
          Number(currencyId) !== Number(systemCurrencyId) &&
          !l.exchange_rate
        ) {
          newErrors[`line_${idx}_exchange_rate`] = "Exchange rate required";
        }
      } else {
        newErrors[`line_${idx}_total`] = "Total required";
      }
    });

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateFiles()) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const submissionData = new FormData();

      submissionData.append("contract", String(contractId));
      submissionData.append("nr", formData.nr);
      submissionData.append("client_document", formData.client_document);
      submissionData.append("sign_date", formData.sign_date);
      submissionData.append("end_date", formData.end_date);
      submissionData.append(
        "responsible_person",
        String(formData.responsible_person || "")
      );
      submissionData.append("client", String(formData.client || ""));
      if (formData.project_code) {
        submissionData.append(
          "project_code",
          String(formData.project_code)
        );
      }
      submissionData.append("object", formData.object);
      submissionData.append("status", formData.status || "Active");

      const linesPayload = lines.map((line) => ({
        opportunity: Number(line.opportunityId),
        currency: line.currency ? Number(line.currency.value) : null,
        exchange_rate: line.exchange_rate ? Number(line.exchange_rate.value) : null,
        total_opportunities_contract:
          line.total === null || line.total === "" ? null : Number(line.total),
      }));

      submissionData.append("lines", JSON.stringify(linesPayload));

      if (files.length > 0) {
        files.forEach((file) => {
          submissionData.append("attachments", file);
        });
      }

      await axiosInstance.post("/client-addendums/", submissionData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      navigate(`/client_contracts/contracts-history/${contractId}`, { replace: true });
    } catch (error) {
      console.error("Save error:", error.response?.data || error.message);

      if (error.response?.data) {
        const err = error.response.data;

        if (
          err.attachments ||
          (typeof err === "object" &&
            JSON.stringify(err).toLowerCase().includes("file"))
        ) {
          showError("File upload error. Please check file type and size.");
        } else if (typeof err === "object") {
          const firstMessage =
            Object.values(err).flat().join(" | ") || JSON.stringify(err);
          showError(firstMessage);
        } else {
          showError("An error occurred while saving.");
        }
      } else {
        showError("An error occurred while saving.");
      }

      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  const ErrorMessage = ({ error }) =>
    error ? (
      <div className="FormClientContract-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    ) : null;

  const SectionHeader = ({ title, section }) => (
    <div
      className="FormClientContract-section-header"
      onClick={() => toggleSection(section)}
    >
      <h2>{title}</h2>
      <button type="button" className="toggle-btn">
        {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="FormClientContract">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading addendum form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="FormClientContract">
      {errorNotification && (
        <div className="FormClientContract-top-error">{errorNotification}</div>
      )}

      <div className="FormClientContract-header">
        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
        <h1 className="header-title">CREATE CLIENT ADDENDUM</h1>
        <div className="contract-number-pill">
          Contract: {parentContract?.nr || "-"}
        </div>
      </div>

      <hr className="title-separator" />

      <form onSubmit={handleSubmit} className="FormClientContract-form">
        <div className="FormClientContract-section">
          <SectionHeader title="ADDENDUM DETAILS" section="contract" />

          {expandedSections.contract && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    PMPC Addendum Nr. <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="nr"
                    value={formData.nr}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, nr: e.target.value }))
                    }
                    className="FormClientContract-input"
                    autoComplete="off"
                  />
                  <ErrorMessage error={fieldErrors.nr} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Client Document <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="client_document"
                    value={formData.client_document}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        client_document: e.target.value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        client_document: "",
                      }));
                    }}
                    className="FormClientContract-input"
                    autoComplete="off"
                    placeholder="Enter client document..."
                  />
                  <ErrorMessage error={fieldErrors.client_document} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Responsible Person <span className="required">*</span>
                  </label>
                  <Select
                    options={employees}
                    classNamePrefix="FormClientContract-select"
                    value={selectedResponsible}
                    onChange={(selectedOption) => {
                      setSelectedResponsible(selectedOption);
                      setFormData((prev) => ({
                        ...prev,
                        responsible_person: selectedOption
                          ? selectedOption.value
                          : "",
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        responsible_person: "",
                      }));
                    }}
                    placeholder="Select responsible person"
                    components={animatedComponents}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.responsible_person} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Client <span className="required">*</span>
                  </label>
                  <Select
                    options={clients}
                    classNamePrefix="FormClientContract-select"
                    value={selectedClient}
                    onChange={handleClientChange}
                    placeholder="Client from parent contract"
                    components={{
                      ...animatedComponents,
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.client} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Project Code
                    {!isFrameworkAgreement && (
                      <>
                        {" "}
                        <span className="required">*</span>
                      </>
                    )}
                  </label>
                  <Select
                    options={projectCodeOptions}
                    classNamePrefix="FormClientContract-select"
                    value={selectedProjectCode}
                    onChange={handleProjectCodeChange}
                    placeholder={
                      isFrameworkAgreement
                        ? "Select project code"
                        : "Project code from parent contract"
                    }
                    components={
                      isFrameworkAgreement
                        ? animatedComponents
                        : {
                          ...animatedComponents,
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }
                    }
                    isClearable={isFrameworkAgreement}
                    isSearchable={isFrameworkAgreement}
                    isDisabled={!isFrameworkAgreement}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.project_code} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Status <span className="required">*</span>
                  </label>
                  <Select
                    options={STATUS_OPTIONS}
                    classNamePrefix="FormClientContract-select"
                    value={selectedStatus}
                    onChange={(selectedOption) => {
                      setSelectedStatus(selectedOption);
                      setFormData((prev) => ({
                        ...prev,
                        status: selectedOption ? selectedOption.value : "",
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        status: "",
                      }));
                    }}
                    components={{
                      ...animatedComponents,
                    }}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                    placeholder="Status from parent contract"
                  />
                  <ErrorMessage error={fieldErrors.status} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Sign Date <span className="required">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={selectedSignDate}
                      onChange={(date) => {
                        setSelectedSignDate(date);
                        setFormData((prev) => ({
                          ...prev,
                          sign_date: date ? format(date, "yyyy-MM-dd") : "",
                        }));
                        setFieldErrors((prev) => ({ ...prev, sign_date: "" }));
                      }}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Select sign date"
                      className="FormClientContract-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>
                          {children}
                        </div>
                      )}
                    />
                    <span
                      className="calendar-icon"
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>
                  <ErrorMessage error={fieldErrors.sign_date} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    End Date <span className="required">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={selectedEndDate}
                      onChange={(date) => {
                        setSelectedEndDate(date);
                        setFormData((prev) => ({
                          ...prev,
                          end_date: date ? format(date, "yyyy-MM-dd") : "",
                        }));
                        setFieldErrors((prev) => ({ ...prev, end_date: "" }));
                      }}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Select end date"
                      className="FormClientContract-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>
                          {children}
                        </div>
                      )}
                    />
                    <span
                      className="calendar-icon"
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>
                  <ErrorMessage error={fieldErrors.end_date} />
                </div>
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  Object <span className="required">*</span>
                </label>
                <textarea
                  name="object"
                  value={formData.object}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, object: e.target.value }));
                    setFieldErrors((prev) => ({ ...prev, object: "" }));
                  }}
                  className="FormClientContract-description"
                  rows={3}
                  placeholder="Enter addendum object..."
                />
                <ErrorMessage error={fieldErrors.object} />
              </div>
            </div>
          )}
        </div>

        <div className="FormClientContract-section">
          <SectionHeader title="OPPORTUNITIES" section="opportunities" />

          {expandedSections.opportunities && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">
                  Select Opportunities
                  {!isFrameworkAgreement && (
                    <>
                      {" "}
                      <span className="required">*</span>
                    </>
                  )}
                </label>

                <Select
                  isMulti
                  options={filteredOpportunitiesByProjectCode}
                  classNamePrefix="FormClientContract-select"
                  value={selectedOpps}
                  onChange={handleOppsMultiChange}
                  placeholder={
                    !formData.project_code
                      ? isFrameworkAgreement
                        ? "No Project Code assigned"
                        : "Select project code first"
                      : isOppLoading
                        ? "Loading opportunities..."
                        : "Search and select opportunities..."
                  }
                  components={animatedComponents}
                  isDisabled={!formData.project_code || isOppLoading}
                  noOptionsMessage={() => "No opportunities found"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                />

                <ErrorMessage error={fieldErrors.lines} />
              </div>

              {lines.length === 0 ? (
                <div className="no-opportunities-message">
                  <AddIcon className="hint-icon" />
                  <span>No opportunities selected yet</span>
                </div>
              ) : (
                <div className="opportunities-grid">
                  {lines.map((line, idx) => {
                    const currencyId = line.currency?.value ?? null;
                    const exchangeOpts = currencyId
                      ? exchangeOptionsByCurrencyId.get(currencyId) || []
                      : [];

                    return (
                      <div key={line.opportunityId} className="opportunity-item">
                        <div className="opportunity-item-header">
                          <div className="item-title">
                            <h3>{line.opportunityLabel}</h3>
                          </div>

                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeOpportunity(line.opportunityId)}
                            title="Remove opportunity"
                          >
                            <DeleteIcon />
                          </button>
                        </div>

                        <div className="opportunity-details">
                          <div className="detail-row single-detail-row">
                            <div className="detail-field">
                              <label className="form-label">Subproject Code</label>
                              <input
                                type="text"
                                value={line.subproject_code_text}
                                readOnly
                                className="readonly-field"
                              />
                            </div>
                          </div>

                          <div className="pricing-row">
                            <div className="pricing-field">
                              <label className="form-label">Total</label>
                              <NumericFormat
                                value={line.total}
                                onValueChange={(values) => {
                                  const v =
                                    values.floatValue !== undefined
                                      ? values.floatValue
                                      : null;
                                  updateLine(line.opportunityId, { total: v });
                                }}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                allowNegative={false}
                                placeholder="0,00"
                                className="amount-input"
                              />
                              <ErrorMessage error={fieldErrors[`line_${idx}_total`]} />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">Currency</label>
                              <Select
                                options={currencies}
                                classNamePrefix="FormClientContract-select"
                                value={line.currency}
                                onChange={(opt) =>
                                  updateLine(line.opportunityId, { currency: opt })
                                }
                                placeholder="Select"
                                components={animatedComponents}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                              />
                              <ErrorMessage error={fieldErrors[`line_${idx}_currency`]} />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">Exchange Rate</label>
                              <Select
                                options={exchangeOpts}
                                classNamePrefix="FormClientContract-select"
                                value={line.exchange_rate}
                                onChange={(opt) =>
                                  updateLine(line.opportunityId, {
                                    exchange_rate: opt,
                                  })
                                }
                                placeholder={
                                  !line.currency
                                    ? "Select currency first"
                                    : Number(currencyId) === Number(systemCurrencyId)
                                      ? "Not needed"
                                      : exchangeOpts.length
                                        ? "Select rate"
                                        : "No rates"
                                }
                                components={animatedComponents}
                                isClearable
                                isDisabled={
                                  !line.currency ||
                                  Number(currencyId) === Number(systemCurrencyId)
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                formatOptionLabel={(option, { context }) =>
                                  context === "value"
                                    ? option.label
                                    : `${option.label}${option.dateLabel
                                      ? ` (${formatDateLabel(option.dateLabel)})`
                                      : ""
                                    }`
                                }
                                styles={selectPortalStyles}
                              />
                              <ErrorMessage
                                error={fieldErrors[`line_${idx}_exchange_rate`]}
                              />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">
                                System Total
                                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                              </label>
                              <NumericFormat
                                value={line.system_total}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                placeholder="0,00"
                                className="amount-input"
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="FormClientContract-section">
          <SectionHeader title="ATTACHMENTS" section="attachments" />

          {expandedSections.attachments && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">Attachments</label>

                <div key={dropzoneKey} className="dropzone-wrapper">
                  <div {...getRootProps({ className: "dropzone" })}>
                    <input {...getInputProps()} />
                    <div className="dropzone-content">
                      {isDragActive ? (
                        <p>Drop the files here...</p>
                      ) : (
                        <p>Drag & drop files or click to select</p>
                      )}
                      <CloudUploadIcon style={{ fontSize: 40, color: "#888" }} />
                    </div>
                  </div>

                  {files.length > 0 && (
                    <div className="files-preview-container">
                      <h4>Selected Files ({files.length})</h4>
                      <div className="files-list">
                        {files.map((file, index) => (
                          <div key={index} className="file-preview-item">
                            <div className="file-info">
                              {getFileIcon(file)}
                              <span
                                className="file-name clickable"
                                title={
                                  file.type === "application/pdf"
                                    ? "Click to preview PDF"
                                    : "Click to download file"
                                }
                                onClick={() => handleFileClick(file)}
                              >
                                {file.name.length > 40
                                  ? `${file.name.substring(0, 40)}...`
                                  : file.name}
                              </span>
                              <span className="file-size">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>

                            <button
                              type="button"
                              className="remove-file-button"
                              onClick={() => handleRemoveFile(index)}
                              title="Remove file"
                            >
                              <DeleteIcon fontSize="small" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <p className="file-hint">
                  Accepted file types: PDF, Word, Excel, Images, Text. Multiple files
                  allowed (max 100MB per file).
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="contract-total-preview">
          <div className="preview-line">
            <span className="contract-total-label">Total Addendum:</span>
            <span className="contract-total-value">{totalAddendumDisplay}</span>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button type="submit" className="primary-btn" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateClientAddendum;